// src/components/CampaignWizard/StepChannels.tsx
import React, { useEffect } from "react";
import {
	Row,
	Col,
	FormGroup,
	Label,
	Input,
	FormText,
	Spinner,
} from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { fetchTemplates } from "store/ctemplates/actions";
import { ApplicationState } from "store";

// ── 1) Central definition of your channel types & labels ─────────────────
export type ChannelType = "EMAIL" | "SMS" | "IN_APP";
export const CHANNEL_LABELS: Record<ChannelType, string> = {
	EMAIL: "Email",
	SMS: "SMS",
	IN_APP: "In-App",
};

// ── 2) Props exactly match what CampaignWizardModal will pass ─────────────
export interface StepChannelsProps {
	data: {
		channels: Record<ChannelType, boolean>;
		templateByChannel: Partial<Record<ChannelType, string>>;
	};
	onChange: (d: {
		/** You can patch just `channels` */
		channels?: Record<ChannelType, boolean>;
		/** —and/or patch just some of the templates */
		templateByChannel?: Partial<Record<ChannelType, string>>;
	}) => void;
	onNext: () => void;
	onBack: () => void;
}

const StepChannels: React.FC<StepChannelsProps> = ({
	data,
	onChange,
	onNext,
	onBack,
}) => {
	const dispatch = useDispatch<any>();
	const { list: templates, loading } = useSelector(
		(s: ApplicationState) => s.template
	);

	useEffect(() => {
		dispatch(fetchTemplates());
	}, [dispatch]);

	const toggleChannel = (ch: ChannelType) => {
		const newChannels = { ...data.channels, [ch]: !data.channels[ch] };
		const newTemplates = { ...data.templateByChannel };
		if (!newChannels[ch]) {
			delete newTemplates[ch];
		}
		onChange({ channels: newChannels, templateByChannel: newTemplates });
	};

	const selectTemplate = (ch: ChannelType) => (
		opt: { value: string; label: string } | null
	) => {
		onChange({
			templateByChannel: {
				...data.templateByChannel,
				[ch]: opt?.value,
			},
		});
	};

	return (
		<>
			<Row className="mb-4">
				<Col>
					{(Object.keys(CHANNEL_LABELS) as ChannelType[]).map((ch) => (
						<FormGroup check inline key={ch}>
							<Input
								type="checkbox"
								id={`channel-${ch}`}
								checked={data.channels[ch]}
								onChange={() => toggleChannel(ch)}
							/>
							<Label check htmlFor={`channel-${ch}`} className="ms-2">
								Enable <strong>{CHANNEL_LABELS[ch]}</strong>
							</Label>
							<FormText color="muted">
								Deliver via {CHANNEL_LABELS[ch].toLowerCase()}.
							</FormText>
						</FormGroup>
					))}
				</Col>
			</Row>

			{(Object.keys(CHANNEL_LABELS) as ChannelType[]).map(
				(ch) =>
					data.channels[ch] && (
						<Row key={ch} className="mb-4">
							<Col md={6}>
								<FormGroup>
									<Label for={`template-select-${ch}`}>
										{CHANNEL_LABELS[ch]} Template
									</Label>
									{loading ? (
										<div>
											<Spinner size="sm" /> Loading…
										</div>
									) : (
										<Select
											inputId={`template-select-${ch}`}
											options={templates
												.filter((t) => t.channelType === ch)
												.map((t) => ({ value: t.id, label: t.name }))}
											value={
												data.templateByChannel[ch]
													? {
														value: data.templateByChannel[ch]!,
														label:
															templates.find(
																(t) => t.id === data.templateByChannel[ch]
															)?.name || "…",
													}
													: null
											}
											onChange={selectTemplate(ch)}
											placeholder={`Select ${CHANNEL_LABELS[ch]} template…`}
											isClearable
										/>
									)}
									<FormText color="muted">
										Pick which {CHANNEL_LABELS[ch].toLowerCase()} template
										this campaign should use.
									</FormText>
								</FormGroup>
							</Col>
						</Row>
					)
			)}
		</>
	);
};

export default StepChannels;
